<template>
  <div :class="['alt-form-group',{'form-error': errorMessage}]">
    <label :for="id">{{ label }} <span v-if="required && label" class="required">*</span></label>
    <div class="input-container" :class="{'relative': prefix}">
        <div v-if="type === 'date'" class="datepicker-container">
          <Datepicker
            v-model="localModelValue"
            placeholder="dd/mm/yyyy"
            :enable-time-picker="false"
            :format="format"
             :hide-icon="hideIcon"
            :id="id"
            :ui="{ input: 'form-control' }"
            position="left"
            @update:modelValue="handleDateUpdate"
            auto-apply
            class="datepicker"
            style=" --dp-primary-color:  #c62556 !important;"
            :min-date="computedMinDate"
            :disabled-dates="disabledDates"
          />
        </div>
        <div v-else-if="type === 'birthday'" class="datepicker-container">
          <Datepicker
            v-model="localModelValue"
            placeholder="Date"
            :enable-time-picker="false"
            :format="format"
             :hide-icon="hideIcon"
            :id="id"
            :ui="{ input: 'form-control' }"
            position="left"
            @update:modelValue="handleDateUpdate"
            auto-apply
            class="datepicker"
            style=" --dp-primary-color:  #c62556 !important;"
            :max-date="maxDate"
            :min-date="new Date(1924, 1, 1)"
            @open="setActiveDate"
            :year-range="[1924,maxYears]"
          />
        </div>
        <!-- Textarea input -->
        <textarea
          v-else-if="type === 'textarea'"
          :id="id"
          :placeholder="placeholder"
          class="form-control"
          :value="modelValue"
          @input="handleInputChange"
        ></textarea>
        <!-- Regular input -->
        <input
          v-else-if ="type === 'phone'"
          :id="id"
          type="tel"
          :placeholder="placeholder"
           @keypress="PreventNumber"
          :value="modelValue"
          class="form-control"
          @input="handleInputChange"
        />
        <span v-if="prefix" class="prefix">{{ prefix }}</span>
        <input
          v-else-if=" type !== 'date' && type !== 'textarea' && type !== 'birthday'"
          :id="id"
          :type="type || 'text'"
          :placeholder="placeholder"
          :value="modelValue"
          class="form-control"
          @input="handleInputChange"
        />
        <label v-if="errorMessage" :for="id" class="error">{{ errorMessage }}</label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch,onMounted } from 'vue';
import  Datepicker  from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
const localModelValue = ref('');
const valueDate = ref('')
interface Props {
  modelValue: string | number;
  label?: string;
  placeholder: string;
  hideIcon?: boolean;
  required?: boolean;
  type?: string;
  rules?: Record<string, any>;
  errorMessage?: string | null;
  id?: string | undefined;
  minDate?: boolean;
  offsetDays?: number
  autoClear?: boolean;
  formart?: string
  prefix?: string
  disabledDates?: Date[]
}
const maxYears = ref<Number>()
const maxDate = ref<Number>()
  const setActiveDate=()=> {
  const date = new Date();
  maxDate.value= date.setFullYear(date.getFullYear() - 18);
  maxYears.value = date.getFullYear();
  if (!isValidDate(localModelValue.value)) {
  localModelValue.value = maxDate.value;
} else {
  localModelValue.value =localModelValue.value ??  maxDate.value;
}
}
function isValidDate(date: Date) {
  return !isNaN(new Date(date).getTime());
}
const props = defineProps<Props>();
const emit = defineEmits(['update:modelValue','clearError','onOpen']);
onMounted(() => {
  if (props.modelValue) {
    localModelValue.value = new Date(props.modelValue) ?? null;
  }
});
const PreventNumber = (event: any) => {
  event = event || window.event;
  const charCode = event.which || event.keyCode;
  if (charCode > 31 && (charCode < 48 || charCode > 57)) {
    event.preventDefault();
  }
  const inputElement = event.target as HTMLInputElement;
  if (inputElement.value.length >= 8) {
    event.preventDefault();
  }

  return true;
};
const formatDate1 = (date: Date) => {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', day: 'numeric', month: 'long' };
  return date.toLocaleDateString('en-US', options);
};
// const localModelValue = ref(props.modelValue as string);
// In case of a range picker, you'll receive [Date, Date]
const format = (date:any) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    // Handle invalid date gracefully
    return 'Invalid date';
  }
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  if(props?.formart === '1') return `${day}-${month}-${year}`
  if(props?.formart === '2') return formatDate1(date)
  return `${day}/${month}/${year}`;
}

// Computed minimum date (today's date in 'YYYY-MM-DD' format)
// const computedMinDate = computed(() => {
//   if (!props.minDate) return '';
//   const today = new Date();
//   const year = today.getFullYear();
//   const month = (today.getMonth() + 1).toString().padStart(2, '0');
//   const day = today.getDate().toString().padStart(2, '0');
//   return `${year}-${month}-${day}`;
// });

const computedMinDate = computed(() => {
  const today = new Date();
  const offset = props.offsetDays ?? 0; // Default to 0 if `offsetDays` is not provided
  today.setDate(today.getDate() + offset + 1);
  const year = today.getFullYear();
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const day = today.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
});

// Format date to 'DD/MM/YYYY'
const formatDate = (date: Date) => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Update handler for Datepicker
const handleDateUpdate = (value:any) => {
  // emit('update:modelValue', value);
  emit('update:modelValue', formatDate(value));
  emit('clearError')
};

// Update handler for other inputs
const handleInputChange = (event: Event) => {
  const target = event.target as HTMLInputElement;

  emit('update:modelValue', target.value);
  emit('clearError')
};

// Watch for prop changes
// watch(() => props.modelValue, (newValue) => {
//   localModelValue.value = newValue as string;
// });

// Validation logic
const errorMessage = computed(() => props.errorMessage || '');

// const errorMessage = ref<string | null>(null);

// Emit value on mount for both datepicker and other input types
onMounted(() => {
  if (props.type === 'date') {
    if (!props.modelValue) {
      // If no value is provided, set and emit today's date
      localModelValue.value = new Date('');
      // localModelValue.value = null;
    } else {
      // If a value exists, use it
      localModelValue.value = new Date(props.modelValue);
    }
    emit('update:modelValue', formatDate(localModelValue.value)); // Emit formatted date value
  } else {
    emit('update:modelValue', props.modelValue); // Emit other input values on mount
  }
});

</script>

<style scoped lang="scss">
/* Add any specific styling for the datepicker */
.datepicker-container {
  position: relative;
}
.datepicker {
  width: 100%;
}
.prefix{
  position: absolute;
  left: 20px;
  height: 50px;
  vertical-align: middle;
  line-height: 53px;
  color: #CACACB;
}
</style>
